import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Star, Users, Sparkles, ChevronRight, Wrench, GitBranch } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import api from "../lib/api";

const SORTS = [
  { id: "recent",  label: "New" },
  { id: "popular", label: "Popular" },
];

// Tiny helper so "Remix²", "Remix³" render cleanly with unicode superscripts.
const SUPER_DIGITS = ["⁰","¹","²","³","⁴","⁵","⁶","⁷","⁸","⁹"];
function superscript(n) {
  return String(n).split("").map((d) => SUPER_DIGITS[parseInt(d, 10)] || d).join("");
}

// Render a tiny 12-column thumbnail of the level's grid.
function GridThumb({ grid }) {
  const rows = grid?.length || 0;
  const cols = grid?.[0]?.length || 0;
  if (!rows || !cols) return <div className="aspect-[3/2] border border-white/10" />;
  return (
    <div
      className="aspect-[3/2] border border-white/10 grid gap-[1px] p-1 bg-black/40"
      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
    >
      {grid.map((row, r) =>
        row.map((v, c) => {
          const bg =
            v === 1 ? "#00ffff" :
            v === 2 ? "#ffd700" :
            v === 3 ? "#ff007f" :
            v === 4 ? "#b026ff" : "transparent";
          const glow =
            v === 4 ? "0 0 4px rgba(176,38,255,0.9)" :
            v === 3 ? "0 0 3px rgba(255,0,127,0.7)" :
            v === 2 ? "0 0 3px rgba(255,215,0,0.7)" :
            v === 1 ? "0 0 3px rgba(0,255,255,0.7)" : "none";
          return (
            <div
              key={`${r}-${c}`}
              style={{ background: bg, boxShadow: glow }}
            />
          );
        })
      )}
    </div>
  );
}

export default function Community() {
  const [sort, setSort] = useState("recent");
  const [items, setItems] = useState(null);
  const [popular, setPopular] = useState([]);
  // "all" or "week" — the trending tab on the loadouts widget.
  const [popularWindow, setPopularWindow] = useState("all");

  const refresh = useCallback(async () => {
    setItems(null);
    try {
      const r = await api.get(`/community/levels?sort=${sort}&limit=30`);
      setItems(r.data.items || []);
    } catch (_) {
      setItems([]);
    }
  }, [sort]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    api
      .get(`/loadouts/popular?limit=5&window=${popularWindow}`)
      .then((r) => setPopular(r.data?.loadouts || []))
      .catch(() => setPopular([]));
  }, [popularWindow]);

  const equipPopular = async (l) => {
    try {
      if (l.ball_skin) {
        await api.post("/ball-skins/active", { skin_id: l.ball_skin });
      }
      if (l.paddle_skin) {
        await api.post("/cosmetics/skin", { skin: l.paddle_skin });
      }
      if (l.trail_color) {
        await api.post("/ball-skins/trail-color", { color: l.trail_color });
      }
      if (l.paddle_accent) {
        await api.post("/cosmetics/paddle-accent", { color: l.paddle_accent });
      }
      toast.success("Loadout equipped", {
        description: `${l.ball_name} + ${l.paddle_skin}`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't equip — some pieces may be locked");
    }
  };

  const empty = useMemo(() => items && items.length === 0, [items]);

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-4 pt-24 pb-16 fade-in">
        <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 font-mono-cosmic text-[11px] tracking-[0.35em] text-[#00ffff] uppercase mb-2">
              <Users className="w-3.5 h-3.5" />
              Community Board
            </div>
            <h1 className="font-heading font-black uppercase text-3xl sm:text-4xl tracking-tighter">
              Pilot-Built Levels
            </h1>
            <p className="text-white/60 text-sm mt-1">
              Levels crafted by pilots. Share codes, star the ones you love, build your own.
            </p>
          </div>
          <Link
            to="/builder"
            className="btn-neon"
            data-testid="community-build-cta"
          >
            <span className="inline-flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              Open Builder
            </span>
          </Link>
        </header>

        <div className="flex items-center gap-2 mb-4" data-testid="community-sort">
          {SORTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              className={`px-3 py-1.5 border font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition ${
                sort === s.id
                  ? "border-[#00ffff] text-[#00ffff] bg-[#00ffff]/10"
                  : "border-white/15 text-white/60 hover:text-white"
              }`}
              data-testid={`community-sort-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Most-played Loadouts — community popularity board.
            Shows the top 5 ball+paddle combos saved by pilots, with a
            one-tap Equip button. Hidden when no loadouts exist yet. */}
        {(popular.length > 0 || popularWindow === "week") && (
          <section
            className="mb-8 border border-[#b026ff]/30 bg-[#b026ff]/5 p-4"
            data-testid="community-popular-loadouts"
          >
            <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#b026ff]/80 uppercase">
                  // MOST-PLAYED LOADOUTS
                </div>
                <h2 className="font-heading text-xl text-white mt-0.5">
                  What the fleet is rocking
                </h2>
              </div>
              <div className="flex items-center gap-1.5">
                {[
                  { id: "all", label: "ALL TIME" },
                  { id: "week", label: "🔥 THIS WEEK" },
                ].map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => setPopularWindow(w.id)}
                    className={`px-2 py-1 border font-mono-cosmic text-[9px] tracking-[0.25em] uppercase transition ${
                      popularWindow === w.id
                        ? "border-[#b026ff] text-[#b026ff] bg-[#b026ff]/10"
                        : "border-white/15 text-white/55 hover:text-white"
                    }`}
                    data-testid={`popular-loadouts-window-${w.id}`}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {popular.length === 0 && popularWindow === "week" && (
                <div
                  className="col-span-full border border-white/10 bg-black/30 p-6 text-center text-white/40 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase"
                  data-testid="popular-loadouts-empty-week"
                >
                  No new loadouts saved this week yet — check back soon.
                </div>
              )}
              {popular.map((l) => (
                <div
                  key={`${l.ball_skin}::${l.paddle_skin}`}
                  className="border border-white/10 bg-black/40 p-3 flex items-center justify-between gap-3"
                  data-testid={`popular-loadout-${l.ball_skin}-${l.paddle_skin}`}
                >
                  <div className="min-w-0">
                    <div className="font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-white/85 truncate">
                      {l.ball_name}
                    </div>
                    <div className="font-mono-cosmic text-[9px] tracking-[0.2em] uppercase text-white/45 truncate">
                      {l.paddle_skin}
                      {l.trail_color && (
                        <span
                          className="inline-block w-2 h-2 ml-2 align-middle border border-white/20"
                          style={{ background: l.trail_color }}
                          title={`Trail: ${l.trail_color}`}
                        />
                      )}
                    </div>
                    <div className="font-mono-cosmic text-[9px] tabular-nums text-[#b026ff] mt-1">
                      {l.count} pilot{l.count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <button
                    onClick={() => equipPopular(l)}
                    className="px-2.5 py-1.5 border border-[#b026ff] text-[#b026ff] font-mono-cosmic text-[9px] tracking-[0.25em] uppercase hover:bg-[#b026ff]/10 shrink-0"
                    data-testid={`popular-loadout-equip-${l.ball_skin}-${l.paddle_skin}`}
                  >
                    Equip
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {items === null ? (
          <div className="font-mono-cosmic text-[#8b949e] tracking-widest py-16 text-center">
            LOADING LEVELS...
          </div>
        ) : empty ? (
          <div
            className="border border-dashed border-white/15 px-6 py-20 text-center font-mono-cosmic text-[11px] tracking-[0.3em] text-white/40 uppercase"
            data-testid="community-empty"
          >
            // NO PILOT-BUILT LEVELS YET — BE THE FIRST
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="community-grid">
            {items.map((lvl) => (
              <Link
                key={lvl.code}
                to={`/community/${lvl.code}`}
                className="glass p-4 border border-white/10 hover:border-[#00ffff]/60 transition block"
                data-testid={`community-card-${lvl.code}`}
              >
                <GridThumb grid={lvl.grid} />
                <div className="mt-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading uppercase text-sm tracking-tight text-white truncate">
                      {lvl.name}
                    </div>
                    <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/50 truncate">
                      by {lvl.author_name} · {lvl.code}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/30" />
                </div>
                {lvl.remix_of && (
                  <div
                    className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#b026ff]/40 text-[#b026ff] font-mono-cosmic text-[8px] tracking-[0.25em] uppercase"
                    data-testid={`community-remix-chip-${lvl.code}`}
                  >
                    <GitBranch className="w-2.5 h-2.5" />
                    {lvl.remix_depth > 1 ? `Remix${superscript(lvl.remix_depth)}` : "Remix"} of {lvl.remix_of_name || lvl.remix_of}
                  </div>
                )}
                <div className="mt-2 flex items-center gap-3 font-mono-cosmic text-[10px] tracking-[0.2em] uppercase">
                  <span className="inline-flex items-center gap-1 text-[#ff8c1a]">
                    <Flame className="w-3 h-3" /> {lvl.plays}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[#ffd700]">
                    <Star className="w-3 h-3" />
                    {lvl.stars_count > 0 ? `${lvl.stars_avg.toFixed(1)} (${lvl.stars_count})` : "—"}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[#00ffff] ml-auto">
                    <Sparkles className="w-3 h-3" /> {lvl.ball_speed.toFixed(1)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
